import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { constants } from '../../Helpers/constantsFile';
import { buildOrdersView } from './orders.selectors';
import {
  actionErrorCleared,
  actionFailed,
  cancelDialogClosed,
  cancelFailed,
  cancelStarted,
  orderRemoved,
  orderUpdated,
  ordersFailed,
  ordersReceived,
  ordersRequested,
  statusWriteFinished,
  statusWriteStarted,
} from './ordersScreenSlice';

const ORDER_STATUS = { PAID: 'paid', PENDING: 'pending' };

/** Turns any axios failure into one sentence a cashier can act on. */
function readError(error) {
  if (axios.isCancel && axios.isCancel(error)) return null;
  if (!navigator.onLine) return 'No internet connection. Reconnect and try again.';
  return (
    error?.response?.data?.message ||
    'Could not reach the server. Check the shop internet and try again.'
  );
}

/**
 * Everything the orders screen needs from the network, plus the derived view.
 *
 * Only the date range triggers a fetch. Status, waiter and search narrow the
 * loaded set in `buildOrdersView`, which is memoised on its real inputs.
 */
export default function useOrders() {
  const dispatch = useDispatch();

  const token = useSelector((s) => s.login.token);
  const activeUser = useSelector((s) => s.login.activeUser);
  const businessId = activeUser?.business?._id;

  const {
    orders,
    status,
    error,
    range,
    statusFilter,
    staffId,
    search,
    selectedOrderId,
    busyOrderId,
  } = useSelector((s) => s.ordersScreen);

  const authHeader = useMemo(() => ({ authorization: token }), [token]);

  // One in-flight request at a time; a new range cancels the previous fetch.
  const sourceRef = useRef(null);

  const load = useCallback(async () => {
    if (!businessId || !token) return;

    if (sourceRef.current) sourceRef.current.cancel('superseded');
    const source = axios.CancelToken.source();
    sourceRef.current = source;

    dispatch(ordersRequested());

    try {
      const url =
        `${constants.baseUrl}/orders/get-business-orders/${businessId}` +
        `?startDate=${range.start}&endDate=${range.end}`;

      const response = await axios.get(url, {
        headers: authHeader,
        cancelToken: source.token,
      });

      dispatch(ordersReceived(response?.data?.data?.orders || []));
    } catch (err) {
      const message = readError(err);
      if (message) dispatch(ordersFailed(message));
    }
  }, [dispatch, businessId, token, authHeader, range.start, range.end]);

  useEffect(() => {
    load();
    return () => {
      if (sourceRef.current) sourceRef.current.cancel('unmounted');
    };
  }, [load]);

  /**
   * Flips one order between paid and pending.
   *
   * The row is replaced in place from the server's response rather than
   * refetching the range — the list must not jump under the cursor.
   */
  const setOrderStatus = useCallback(
    async (entry, paymentAccount) => {
      if (!entry || entry.isInvoice || busyOrderId) return;

      const next = entry.isPending ? ORDER_STATUS.PAID : ORDER_STATUS.PENDING;
      // Only a payment names an account. Putting an order back to pending
      // clears it, because the money is no longer anywhere.
      const account = next === ORDER_STATUS.PAID ? String(paymentAccount || '').trim() : '';

      dispatch(actionErrorCleared());
      dispatch(statusWriteStarted(entry.id));

      try {
        const response = await axios.patch(
          `${constants.baseUrl}/orders/update-order-status/${entry.id}`,
          next === ORDER_STATUS.PAID
            ? { orderStatus: next, paymentAccount: account }
            : { orderStatus: next, paymentAccount: null },
          { headers: authHeader }
        );

        // The response comes back UNPOPULATED — its `customer` and `user` are
        // bare ids. Merging the whole document would blank the customer and
        // waiter names on the row, so only the field that changed is applied.
        const updated = response?.data?.data?.updatedOrder;

        /**
         * Belt and braces on the account.
         *
         * The status endpoint is where the pay step belongs, and the field is
         * sent there. If a server picks up the status and ignores the account,
         * the answer comes back without it — and rather than silently losing
         * which wallet the money went into, it is written with the ordinary
         * order PATCH instead.
         *
         * `items` is deliberately not sent on that second call: the server
         * builds its kitchen print plan by diffing the items it receives, so
         * sending none means no station hears anything. The same care
         * `billToCustomer` takes below, for the same reason.
         */
        let savedAccount = updated?.paymentAccount;

        if (account && !savedAccount) {
          const retry = await axios.patch(
            `${constants.baseUrl}/orders/${entry.id}`,
            { paymentAccount: account },
            { headers: authHeader }
          );
          savedAccount = retry?.data?.data?.updatedOrder?.paymentAccount;
        }

        dispatch(
          orderUpdated({
            _id: entry.id,
            orderStatus: updated?.orderStatus || next,
            // The server's own value when it gave one, so the band groups by
            // exactly what is stored.
            paymentAccount: savedAccount === undefined ? account : savedAccount || '',
          })
        );
      } catch (err) {
        // A failed flip leaves the row as it was and reports itself in a
        // strip — it must not replace the whole list with an error page.
        const message = readError(err);
        if (message) dispatch(actionFailed(message));
      } finally {
        dispatch(statusWriteFinished());
      }
    },
    [dispatch, authHeader, busyOrderId]
  );

  /**
   * Cancels an order, which on the server also voids the invoice transaction
   * and reverses the customer's balance. A reason is required.
   */
  const cancelOrder = useCallback(
    async (orderId, reason) => {
      const trimmed = String(reason || '').trim();
      if (!orderId || !trimmed) return;

      dispatch(cancelStarted());

      try {
        await axios.delete(`${constants.baseUrl}/orders/cancel-order/${orderId}`, {
          headers: authHeader,
          data: { cancelReason: trimmed },
        });

        dispatch(orderRemoved(orderId));
        dispatch(cancelDialogClosed());
      } catch (err) {
        dispatch(cancelFailed(readError(err) || 'Could not cancel the order.'));
      }
    },
    [dispatch, authHeader]
  );

  /**
   * Moves a cash order onto a customer's account.
   *
   * The server does all of the hard part already: `updateOrder` sees the
   * payment type change, finds no existing transaction and opens one, raising
   * the customer's balance inside the same Mongo session as the order write.
   * Switching a customer, or switching back to cash, is handled there too.
   *
   * `items` is deliberately NOT sent. The server builds its kitchen print plan
   * by diffing the items it receives against the ones it holds, so sending
   * them — even unchanged — is the difference between settling a bill and
   * risking a ticket at a station. Sending none means the plan is empty.
   */
  const billToCustomer = useCallback(
    async (entry, customerId) => {
      if (!entry || !customerId) return;

      dispatch(actionErrorCleared());
      dispatch(statusWriteStarted(entry.id));

      try {
        const response = await axios.patch(
          `${constants.baseUrl}/orders/${entry.id}`,
          { paymentType: 'invoice', customer: customerId },
          { headers: authHeader }
        );

        const updated = response?.data?.data?.updatedOrder;

        // This response populates `customer` but NOT `user`, so merging the
        // whole document would blank the waiter name on the row. Only the two
        // fields that actually changed are applied — the same care
        // `setOrderStatus` takes above, for the same reason.
        dispatch(
          orderUpdated({
            _id: entry.id,
            paymentType: 'invoice',
            customer: updated?.customer || customerId,
          })
        );
      } finally {
        dispatch(statusWriteFinished());
      }
    },
    [dispatch, authHeader]
  );

  const view = useMemo(
    () => buildOrdersView({ orders, statusFilter, staffId, search }),
    [orders, statusFilter, staffId, search]
  );

  const selectedEntry = useMemo(
    () => (selectedOrderId ? view.byId.get(selectedOrderId) || null : null),
    [view.byId, selectedOrderId]
  );

  return {
    view,
    selectedEntry,
    status,
    error,
    range,
    reload: load,
    setOrderStatus,
    cancelOrder,
    billToCustomer,
  };
}
